import urlResolver from 'admin/utils/URLResolver';
import { post } from 'core/utils/request';
import { formatISO } from 'date-fns';
import { getTaskMeta } from 'edrolo/domains/coursePortal/utils';
import useSWR from 'swr';
import { Task, TaskAssignmentStatus } from '../types/Task';

export const useTaskStudent = (taskId: Task['id']) => {
  const {
    data: task,
    error: taskError,
    isLoading: taskIsLoading,
    mutate: mutateTask,
  } = useSWR<Task>(
    urlResolver.reverse('api:student_tasks-detail', {
      taskId,
    })
  );

  const startTask = async () => {
    if (!task) {
      return;
    }

    const { hasStarted, isClosed } = getTaskMeta(task);

    if (hasStarted || isClosed) {
      return;
    }

    const updatedTask = {
      ...task,
      task_assignments: [
        {
          ...task.task_assignments[0],
          started_at: formatISO(new Date()) as DateISO8601,
          completion_status: TaskAssignmentStatus.IN_PROGRESS,
        },
      ],
    };

    try {
      mutateTask(
        async () => {
          const response = await post(
            urlResolver.reverse('api:student_tasks-start', {
              taskId,
            })
          );
          return response;
        },
        {
          optimisticData: updatedTask,
          rollbackOnError: true,
          revalidate: false,
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  const submitTask = async () => {
    if (
      !task ||
      task?.task_assignments[0]?.completion_status ===
        TaskAssignmentStatus.COMPLETED
    ) {
      return;
    }

    const updatedTask = {
      ...task,
      task_assignments: [
        {
          ...task.task_assignments[0],
          submitted_at: formatISO(new Date()) as DateISO8601,
          completion_status: TaskAssignmentStatus.COMPLETED,
        },
      ],
    };

    try {
      mutateTask(
        async () => {
          const response = await post(
            urlResolver.reverse('api:student_tasks-submit', {
              taskId,
            })
          );
          return response;
        },
        {
          optimisticData: updatedTask,
          rollbackOnError: true,
          revalidate: false,
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  return {
    task,
    taskError,
    taskIsLoading,
    startTask,
    submitTask,
  };
};
